/**
 * What the comment box says about @mentions of people who are not on the bid.
 *
 * - The prompt: the author may add them — ask before posting. One click adds
 *   them and posts; Cancel posts nothing and returns to editing.
 * - The note: nobody is adding them, so they won't hear about the comment.
 *   Said plainly rather than dropping the mention silently.
 */

import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { addToBidQuestion, notNotifiedNote, type MentionedPerson } from '@/lib/mention-invites'

interface MentionInviteNoticeProps {
  /** People to confirm adding; null when no prompt is showing. */
  pendingAdd: MentionedPerson[] | null
  /** Mentioned people who won't be notified. */
  unreachable: MentionedPerson[]
  /** "Add and post" / "Add and reply". */
  confirmLabel: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function MentionInviteNotice({
  pendingAdd,
  unreachable,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: MentionInviteNoticeProps) {
  if (!pendingAdd && unreachable.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      {pendingAdd && (
        <div
          role="group"
          aria-label="Add mentioned people to this bid"
          className="flex flex-col gap-2 rounded-lg bg-muted/40 px-3 py-2"
        >
          <p className="text-sm">{addToBidQuestion(pendingAdd)}</p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" onClick={onConfirm} disabled={busy}>
              {busy ? 'Adding…' : confirmLabel}
            </Button>
          </div>
        </div>
      )}
      {unreachable.length > 0 && (
        <p className="flex items-start gap-1.5 text-meta" data-testid="mention-not-notified">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>{notNotifiedNote(unreachable)}</span>
        </p>
      )}
    </div>
  )
}
